import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Dialog, Grid, Typography,
} from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Draggable from 'react-draggable'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import stylesheet from '../detail-container.stylesheet'
import CardLayoutComponent from '../common/card-layout.component'
import QuestionnaireComponent from '../../../questionnaires/questionnaire.component'
import Helper from '../../../../utils/helper'
import { formatDisplayName } from '../../../../utils/display-name-formatter'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'

/**
 * F118 §9.15 — "discoverable" diagnoses: captured Yes/No answers on questionnaires that are
 * NOT a dependent of any enrolled program. The backend emits them, program-agnostically, in
 * `measurements.discoverable_calculators` (editable calc shape, `discoverable: true`). They
 * render here, under the "Other conditions" section, instead of being repeated under each
 * program's Yes/No card.
 *
 * Grouping (Dr. Deo 2026-07-08): Yes expanded by default (up to N), No COLLAPSED — a
 * non-dependent captured No is rare and lower-value (AI chart review yields non-matching
 * Yes's; No's overwhelmingly come from program-specific workup, which stays under its
 * program). See PROGRAM_DATA_SURFACING_RULES.md §9.15.
 */

// Yes group auto-expands only when the captured set is small enough to not overwhelm.
const YES_EXPAND_LIMIT = 10

interface IProps {
  classes: ClassNameMap
}

const DiscoverableConditionsComponent: React.FC<IProps> = ({ classes }) => {
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const { updatePatientData } = useUpdateDataAction(patientDetail.id)

  const [selectedCal, setSelectedCal] = useState({} as any)
  const [open, setOpen] = useState(false)
  const [isSubmit, setIsSubmit] = useState(false)
  const [isConfirmSubmit, setIsConfirmSubmit] = useState(false)
  const calRef = useRef(null) as any

  const discoverable = useMemo(
    () => (patientDetail?.measurements?.discoverable_calculators || []) as any[],
    [patientDetail]
  )

  const { yesGroup, noGroup } = useMemo(() => {
    const yes: any[] = []
    const no: any[] = []
    discoverable.forEach((d: any) => {
      if (d.first_response === 'Yes') yes.push(d)
      else if (d.first_response === 'No') no.push(d)
    })
    const byName = (a: any, b: any) =>
      (formatDisplayName(a?.display_name) || '').toLowerCase()
        .localeCompare((formatDisplayName(b?.display_name) || '').toLowerCase())
    return { yesGroup: yes.sort(byName), noGroup: no.sort(byName) }
  }, [discoverable])

  const handleClickOpen = (_id: string) => {
    setSelectedCal(discoverable.find((d: any) => d.id === _id) || {})
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setIsSubmit(false)
    setIsConfirmSubmit(false)
    setSelectedCal({})
  }

  const handleIsSubmit = (flag: boolean) => setIsSubmit(flag)

  // On save the answer may change (Yes<->No) or drop out of the discoverable set entirely
  // (e.g. -> Unknown, or the questionnaire becomes a program dependent). A full measurement
  // refetch lets the backend recompute the bucket rather than mutating it locally.
  const handleSave = () => {
    handleClose()
    updatePatientData({ isMeasurement: true })
  }

  const getDiagnosisRes = (d: any) =>
    d.first_response === 'Yes' || d.first_response === 'No' ? `(${d.first_response})` : ''

  const getQuestionnaireTitle = (currQ: any) => {
    const name = Helper.toCamelCaseFirstWord(
      currQ?.display_name ? formatDisplayName(currQ.display_name) : currQ?.name
    )
    return `${name} ${getDiagnosisRes(currQ)}`
  }

  const renderCard = (d: any) => (
    <CardLayoutComponent
      key={`discoverable-${d.id}`}
      id={d.id}
      name={d?.name}
      leftHeader={formatDisplayName(d?.display_name)}
      rightHeader={<></>}
      subHeader={getDiagnosisRes(d)}
      bottomText={d.created_on ? moment.unix(d.created_on).format('MM/DD/YY') : ''}
      needs_confirmation={false}
      needs_update={false}
      icons={[{ iconType: 'edit', callback: handleClickOpen }]}
      programs={[]}
      entityRef={'calculator'}
      patientId={patientDetail.id}
      category={'conditions'}
      is_referred={false}
      is_overridden={false}
      onRefClick={() => {}}
    />
  )

  const renderGroup = (label: string, items: any[], defaultExpanded: boolean) => {
    if (!items?.length) return null
    return (
      <Accordion square defaultExpanded={defaultExpanded} className={classes.patientAccrodionBg}>
        <AccordionSummary expandIcon={<ExpandMoreIcon className={classes.patientAccrodionExpandIcon} />}>
          <Typography className={classes.subTitle}>{`${label} (${items.length})`}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container>
            <Grid item className={classes.fullWidth}>
              {items.map((d: any) => renderCard(d))}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  useEffect(() => {
    // keep the edit dialog's selected calc fresh if patient data refetches while open
    if (open && selectedCal?.id) {
      const fresh = discoverable.find((d: any) => d.id === selectedCal.id)
      if (fresh) setSelectedCal(fresh)
    }
  }, [discoverable])

  if (!yesGroup.length && !noGroup.length) return null

  return (
    <>
      <Grid container direction="row" sx={{ paddingLeft: '0.2rem' }}>
        <Grid item className={classes.fullWidth}>
          {renderGroup('Captured — Yes', yesGroup, yesGroup.length <= YES_EXPAND_LIMIT)}
        </Grid>
        <Grid item className={classes.fullWidth}>
          {renderGroup('Captured — No', noGroup, false)}
        </Grid>
      </Grid>
      {open && (
        <Draggable ref={calRef} defaultPosition={{ x: 280, y: 50 }} handle=".questionnarie-dialog">
          <Dialog
            id={'discoverable-cal-id'}
            fullWidth={true}
            maxWidth="md"
            disableEscapeKeyDown
            open={open}
            onClose={handleClose}
            aria-labelledby="discoverable-questionnaire"
            hideBackdrop
            disableScrollLock
            onKeyUp={(e) => {
              e.preventDefault()
              if (e.key === 'Enter') setIsConfirmSubmit(true)
            }}
            sx={{ position: 'absolute', top: '10%' }}
          >
            <QuestionnaireComponent
              data={{
                name: Helper.toCamelCaseFirstWord(formatDisplayName(selectedCal?.display_name) || selectedCal?.name),
                title: getQuestionnaireTitle(selectedCal),
                severity: { ...selectedCal?.severity },
                category: selectedCal.category,
              }}
              pId={patientDetail.id + ''}
              qId={selectedCal?.questionnaire_id + ''}
              qu={selectedCal.id}
              handleIsSubmit={handleIsSubmit}
              handleClose={handleClose}
              handleSave={handleSave}
              isConfirmSubmit={isConfirmSubmit}
              isSubmit={isSubmit}
              needsConfirmation={selectedCal?.needs_confirmation}
              presetIntent={(() => {
                const v = String(selectedCal?.first_response ?? '')
                if (v === 'Yes') return 'confirm'
                if (v === 'No') return 'deny'
                return undefined
              })()}
            />
          </Dialog>
        </Draggable>
      )}
    </>
  )
}

export default withStyles(stylesheet)(DiscoverableConditionsComponent)
